"use client";

import HeadingSize from "@/components/ui/HeadingSize";
import ProductCard from "@/components/ui/product-card";
import { COLLECTION_NAMES } from "@/constants/collections";
import { useCart } from "@/contexts/cart-context";
import { EventType, type IAddToCartEvent, publishEvent } from "@/events";
import { CartStorage } from "@/libs/cart-storage";
import { getRatingFromMetafields } from "@/libs/metafield-utils";
import { convertPaisaToRupees } from "@/libs/utils";
import { createCart, shopifyAddToCart } from "@/shopify/api/shopify-cart";
import { fetchShopifyCollectionByHandle } from "@/shopify/api/shopify-collection";
import type { ICartProductInfo } from "@/types/cart";
import type { IApiProduct } from "@/types/collection";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ProductCardSkeleton from "../ui/ProductCardSkeleton";

export default function FindYourAtomSection() {
  const { t, i18n } = useTranslation();

  const [products, setProducts] = useState<IApiProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { openCart } = useCart();

  useEffect(() => {
    const fetchShopifyProducts = async () => {
      try {
        setIsLoading(true);
        const collection =
          await fetchShopifyCollectionByHandle(COLLECTION_NAMES);
        if (collection?.products?.edges?.length > 0) {
          const transformedProducts = collection.products.edges
            .filter(({ node }: any) => node.availableForSale)
            .slice(0, 4)
            .map(({ node }: any) => {
              const firstVariant = node.variants.edges[0]?.node;
              const firstImage = node.images.edges[0]?.node;
              const rating = getRatingFromMetafields(node.metafields);
              // Find the Hindi title metafield if it exists
              const titleHiMetafield = node.metafields?.find(
                (metafield: any) =>
                  metafield?.namespace === "translations" &&
                  metafield?.key === "title_hi",
              );

              // Use Hindi title if language is Hindi and metafield exists, otherwise use English title
              const title =
                i18n.language === "hi" && titleHiMetafield
                  ? titleHiMetafield.value
                  : node.title;
              return {
                id: node.id,
                title: title,
                handle: node.handle,
                description: node.description,
                rating,
                price: Number.parseFloat(
                  node.priceRange.minVariantPrice.amount,
                ),
                compareAtPrice: firstVariant?.compareAtPrice
                  ? Number.parseFloat(firstVariant.compareAtPrice.amount)
                  : undefined,
                image_url: firstImage?.url || "",
                availableForSale: node.availableForSale,
                variant: firstVariant
                  ? {
                      id: firstVariant.id,
                      title: firstVariant.title,
                      price: (
                        Number.parseFloat(firstVariant.price.amount) * 100
                      ).toString(),
                      compare_at_price: firstVariant.compareAtPrice?.amount
                        ? (
                            Number.parseFloat(
                              firstVariant.compareAtPrice.amount,
                            ) * 100
                          ).toString()
                        : undefined,
                      sku: firstVariant.sku,
                      length: node.variants.edges.length,
                    }
                  : undefined,
              };
            });
          setProducts(transformedProducts);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchShopifyProducts();
  }, [i18n.language]);

  const createAtomProductInfo = (
    product: IApiProduct,
    variantId: string,
  ): ICartProductInfo => ({
    title: product.title,
    price: String(product.variant?.price || product.price),
    image: product.image_url
      ? {
          id: product.id,
          url: product.image_url,
          alt_text: product.title,
          is_main: true,
          position: 1,
        }
      : undefined,
    slug: product.handle,
    variant: {
      id: variantId,
      title: product.variant?.title || product.title,
    },
  });

  const syncAtomWithShopifyCart = async (variantId: string) => {
    try {
      const existingCartId = CartStorage.getCartToken();
      let cartId: string;

      if (existingCartId) {
        cartId = existingCartId;
      } else {
        const newCart = await createCart();
        if (newCart?.id) {
          cartId = newCart.id;
          CartStorage.saveCartToken(cartId);
        } else {
          console.error("Failed to create a new cart");
          return;
        }
      }

      const updatedCart = await shopifyAddToCart(variantId, 1, cartId);
      if (updatedCart?.lines?.edges) {
        const shopifyItems = updatedCart.lines.edges.map(
          (edge: any) => edge.node,
        );
        CartStorage.saveShopifyCartItems(shopifyItems);
      }

      openCart();
    } catch (error) {
      console.error("Error syncing with Shopify cart:", error);
    }
  };

  const handleAddToCart = async (product: IApiProduct) => {
    if (!product?.variant || product?.variant?.length === 0) {
      console.error("Product or variant not available");
      return;
    }

    const selectedVariant = product.variant;
    const variantId = selectedVariant?.id;
    if (!variantId) {
      console.error("Variant ID not available");
      return;
    }

    const productInfo = createAtomProductInfo(product, variantId);
    const event: IAddToCartEvent = {
      type: EventType.ADD_TO_CART,
      productId: product.id,
      productName: product.title,
      price: convertPaisaToRupees(+selectedVariant.price),
      currency: "INR",
      timestamp: Date.now(),
      variant: selectedVariant?.title || "",
    };

    publishEvent(event);
    await syncAtomWithShopifyCart(variantId);
  };
  return (
    <section className="bg-white py-4 sm:py-4 md:py-32 lg:pt-16 lg:pb-24">
      <div className="container-custom px-4 sm:px-6 max-w-7xl">
        <div className="text-center mb-6 sm:mb-8 md:mb-8">
          <HeadingSize text={t("home.findYourRyze")} />
        </div>

        <div className="flex flex-row overflow-x-auto scrollbar-hide gap-5">
          {isLoading ? (
            <div className="text-center py-4 sm:py-6 md:py-8 text-gray-500">
              {t("common.loadingProducts")}
            </div>
          ) : products.length > 0 ? (
            <div className="flex flex-row  overflow-x-auto   scrollbar-hide gap-5 lg:mx-auto">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="max-w-5xl mx-auto flex items-center justify-between gap-4"
                >
                  <ProductCard
                    key={product.id}
                    title={product.title}
                    price={product?.variant?.price || "0"}
                    mrp={product?.variant?.compare_at_price || "0"}
                    image={product.image_url || "/placeholder-product.png"}
                    onAddToCart={() => handleAddToCart(product)}
                    handle={product.handle}
                    rating={product.rating}
                    inStock={product.availableForSale}
                    isLoading={isLoading}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 sm:py-6 md:py-8 text-gray-500">
              {t("common.noProductsFound")}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
